import {
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppDialog, { DialogField } from '../components/AppDialog';
import { useAuth } from '../contexts/AuthContext';
import { useGroups } from '../contexts/GroupContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useWorkspaces } from '../contexts/WorkspaceContext';
import { useShareIcons } from '../hooks/useShareIcons';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  getPersonalItems,
  getWorkspaceItems,
  updateItemShare,
} from '../services/factCheckItemService';
import {
  createInvite,
  deleteGroup,
  getGroupMembers,
  removeGroupMember,
} from '../services/groupService';
import { createWorkspace } from '../services/workspaceService';
import { IMG } from '../theme/assets';
import type { FactCheckItem, GroupMember, Workspace } from '../types';

/** 使い方の3手順。グループが1つも無い人には開いた状態で出す。 */
const STEPS = [
  { icon: 'stepGroup', title: 'グループを作る', desc: '番組名や班の名前で。例「制作1班」' },
  { icon: 'stepInvite', title: '人を招く', desc: '招待リンクを送ると参加できます' },
  {
    icon: 'stepProject',
    title: '案件を作る',
    desc: '放送回ごとに箱を分けてアイテムを入れます',
  },
] as const;

/** まだグループが無い人に形を見せるための見本。
 *  題材を持たせると本物の編集判断に見えてしまうので、中身のない名前にしてある。 */
const SAMPLE_PROJECTS = [
  {
    id: 'sample',
    name: '見本の案件',
    doc: 2,
    video: 1,
    total: 3,
    mine: 2,
    date: '2026-09-13T15:40:00Z',
    description: 'この案件に入れたアイテムが、グループのメンバー全員から見えます。',
  },
  {
    id: 'sample2',
    name: '見本の案件 2',
    doc: 3,
    video: 0,
    total: 3,
    mine: 0,
    date: '2026-09-06T11:02:00Z',
  },
  {
    id: 'sample3',
    name: '見本の案件 3',
    doc: 2,
    video: 0,
    total: 2,
    mine: 1,
    date: '2026-08-30T09:45:00Z',
  },
];

const WorkspaceList: React.FC = () => {
  const navigate = useNavigate();
  // /share/:id で開いたときは、その案件の中身を真ん中に出す
  const { id: openProjectId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { groups, refreshGroups } = useGroups();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();
  const icons = useShareIcons();
  const { workspaces, loading, refreshWorkspaces } = useWorkspaces();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const [members, setMembers] = useState<Record<string, GroupMember[]>>({});
  /** 案件ごとの中身。アイテム一覧に createdBy と type が付いてくるので追加の読み込みは要らない */
  type Summary = {
    total: number;
    mine: number;
    doc: number;
    video: number;
    lastAdded: string | null;
  };
  const [summaries, setSummaries] = useState<Record<string, Summary>>({});
  const [projectItems, setProjectItems] = useState<FactCheckItem[] | null>(null);
  // 案件から自分のアイテムを選んで共有する
  const [shareOpen, setShareOpen] = useState(false);
  const [myItems, setMyItems] = useState<FactCheckItem[] | null>(null);
  const [picked, setPicked] = useState<Record<string, { factCheck: boolean; compliance: boolean }>>(
    {},
  );
  const [sharing, setSharing] = useState(false);

  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const noGroups = groups.length === 0;

  // 左で選んだグループ。最初のものを既定にする
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId && groups.length > 0) setSelectedId(groups[0].id);
  }, [groups, selectedId]);
  const current = groups.find((g) => g.id === selectedId) ?? groups[0] ?? null;
  const isOwner = !!current && !!user && current.ownerId === user.uid;

  // グループを切り替えたら、前のグループの招待リンクは消す
  // biome-ignore lint/correctness/useExhaustiveDependencies: 切り替えたことだけを見たい
  useEffect(() => {
    setInviteUrl(null);
  }, [current?.id]);

  const openProject = openProjectId
    ? (workspaces.find((w: Workspace) => w.id === openProjectId) ?? null)
    : null;

  // 開いている案件の中身。案件を切り替えるたびに取り直す
  useEffect(() => {
    if (!openProjectId) {
      setProjectItems(null);
      return;
    }
    let alive = true;
    setProjectItems(null);
    (async () => {
      try {
        const list = await getWorkspaceItems(openProjectId);
        if (alive) setProjectItems(list);
      } catch {
        if (alive) setProjectItems([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [openProjectId]);

  // 案件を開いたら、その案件を持つグループを左で選んでおく
  useEffect(() => {
    if (openProject?.groupIds?.length) {
      setSelectedId((prev) => prev ?? openProject.groupIds[0]);
    }
  }, [openProject]);

  const openShareDialog = async () => {
    setShareOpen(true);
    setPicked({});
    if (myItems) return;
    try {
      setMyItems(await getPersonalItems());
    } catch {
      setMyItems([]);
    }
  };

  const handleSharePicked = async () => {
    if (!openProjectId) return;
    const entries = Object.entries(picked).filter(([, v]) => v.factCheck || v.compliance);
    if (entries.length === 0) return;
    try {
      setSharing(true);
      for (const [itemId, v] of entries) {
        await updateItemShare(itemId, openProjectId, v);
      }
      setShareOpen(false);
      setProjectItems(await getWorkspaceItems(openProjectId));
    } catch (e) {
      console.error('共有に失敗しました:', e);
    } finally {
      setSharing(false);
    }
  };

  const handleInvite = async (groupId: string) => {
    if (!user) return;
    try {
      if (inviteUrl) {
        await navigator.clipboard.writeText(inviteUrl);
        return;
      }
      setInviting(true);
      const invite = await createInvite(groupId, user.uid);
      const url = `${window.location.origin}/invite/${groupId}/${invite.id}`;
      setInviteUrl(url);
      await navigator.clipboard.writeText(url);
    } catch (e) {
      console.error('招待リンクの作成に失敗しました:', e);
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (groupId: string, userId: string) => {
    if (!window.confirm('このメンバーをグループから外しますか？')) return;
    try {
      await removeGroupMember(groupId, userId);
      setMembers((prev) => ({
        ...prev,
        [groupId]: (prev[groupId] ?? []).filter((m) => m.userId !== userId),
      }));
    } catch (e) {
      console.error('メンバーの削除に失敗しました:', e);
    }
  };

  const handleDeleteGroup = async (groupId: string, name: string) => {
    if (!window.confirm(`グループ「${name}」を削除しますか？この操作は元に戻せません。`)) return;
    try {
      await deleteGroup(groupId);
      setSelectedId(null);
      await refreshGroups();
      await refreshWorkspaces();
    } catch (e) {
      console.error('グループの削除に失敗しました:', e);
    }
  };

  useEffect(() => {
    if (groups.length === 0) return;
    let alive = true;
    (async () => {
      const entries = await Promise.all(
        groups.map(async (g) => {
          try {
            return [g.id, await getGroupMembers(g.id)] as const;
          } catch {
            return [g.id, [] as GroupMember[]] as const;
          }
        }),
      );
      if (alive) setMembers(Object.fromEntries(entries));
    })();
    return () => {
      alive = false;
    };
  }, [groups]);

  // 案件ごとの中身をまとめる。件数が少ないので素直に1件ずつ数える
  useEffect(() => {
    if (workspaces.length === 0) return;
    let alive = true;
    (async () => {
      const entries = await Promise.all(
        workspaces.map(async (w) => {
          try {
            const items = await getWorkspaceItems(w.id);
            const dates = items
              .map((i) => i.createdAt)
              .filter(Boolean)
              .sort();
            return [
              w.id,
              {
                total: items.length,
                mine: user ? items.filter((i) => i.createdBy === user.uid).length : 0,
                doc: items.filter((i) => i.type === 'document').length,
                video: items.filter((i) => i.type.startsWith('video')).length,
                lastAdded: dates.length ? dates[dates.length - 1] : null,
              },
            ] as const;
          } catch {
            return [w.id, { total: 0, mine: 0, doc: 0, video: 0, lastAdded: null }] as const;
          }
        }),
      );
      if (alive) setSummaries(Object.fromEntries(entries));
    })();
    return () => {
      alive = false;
    };
  }, [workspaces, user]);

  const handleCreateWorkspace = async () => {
    if (!user || !newName.trim() || selectedGroupIds.length === 0) return;
    try {
      setCreating(true);
      await createWorkspace(user.uid, {
        name: newName.trim(),
        description: newDescription.trim(),
        groupIds: selectedGroupIds,
      });
      setCreateDialogOpen(false);
      setNewName('');
      setNewDescription('');
      setSelectedGroupIds([]);
      await refreshWorkspaces();
    } catch (e) {
      console.error('案件の作成に失敗しました:', e);
    } finally {
      setCreating(false);
    }
  };

  const openCreateFor = useCallback((groupId: string) => {
    setSelectedGroupIds([groupId]);
    setCreateDialogOpen(true);
  }, []);

  /** 「9/13 15:40」。日付だけだと、いつ入ったものか掴みづらいので時間まで出す */
  const formatDateTime = (s: string) => {
    const d = new Date(s);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`;
  };

  const cardSx = {
    borderRadius: '13px',
    border: `1px solid ${tokens.line}`,
    background: tokens.paper,
    boxShadow:
      themeMode === 'dark' ? '0 6px 18px rgba(0,0,0,.18)' : '0 6px 18px rgba(30,30,70,.05)',
  } as const;

  const smallBtn = {
    textTransform: 'none',
    fontWeight: 800,
    fontSize: 12,
    borderRadius: '9px',
    px: 1.5,
    py: 0.75,
    minWidth: 0,
    whiteSpace: 'nowrap',
  } as const;

  const paneHeadSx = {
    fontSize: 9.5,
    fontWeight: 900,
    letterSpacing: '.09em',
    color: tokens.faint,
    px: 1.75,
    pt: 1.75,
    pb: 0.75,
  } as const;

  const gradBtn = {
    color: '#fff',
    background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
    '&:hover': { background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})` },
  } as const;

  /** 左の行。選んでいるものは帯で示す */
  const groupRowSx = (on: boolean) =>
    ({
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: 1.75,
      py: 1.1,
      cursor: 'pointer',
      borderLeft: `3px solid ${on ? tokens.teal : 'transparent'}`,
      background: on ? `${tokens.teal}14` : 'transparent',
      '&:hover': { background: `${tokens.teal}14` },
    }) as const;

  const avatar = (name: string, color: string) => (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: color,
        color: '#fff',
        fontSize: 11.5,
        fontWeight: 900,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
    >
      {name.charAt(0)}
    </Box>
  );

  /** 案件の中のアイテム1枚。共有されている結果だけを出す */
  const sharedItemCard = (it: FactCheckItem) => {
    const sh = it.shares?.[openProjectId ?? ''] ?? { factCheck: false, compliance: false };
    const isVideo = it.type.startsWith('video');
    const kind = isVideo ? '動画' : '文書';
    const bad = it.complianceBadCount ?? 0;
    const caution = it.complianceCautionCount ?? 0;
    return (
      <Box key={it.id} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.75,
            py: 1.5,
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '10px',
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              background: isVideo
                ? 'linear-gradient(135deg, #2b3350, #1b2133)'
                : 'linear-gradient(135deg, #334066, #232a44)',
            }}
          >
            <Box component="img" src={icons.itemKind} alt="" sx={{ width: 23, height: 23 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 800,
                color: tokens.ink,
                lineHeight: 1.45,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {it.title}
            </Typography>
            <Typography sx={{ fontSize: 10, color: tokens.faint, mt: 0.4 }}>
              {kind} ・ {formatDateTime(it.createdAt)}
            </Typography>
          </Box>
        </Box>
        {/* 同じファイルなのでカードは1枚。見たい結果をボタンで選んで開く */}
        <Box sx={{ px: 1.75, py: 1.5, display: 'flex', gap: 1 }}>
          {sh.factCheck && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/items/${it.id}`)}
              sx={{
                ...smallBtn,
                flex: 1,
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.25,
                py: 1,
                color: tokens.ink,
                borderColor: tokens.line,
              }}
            >
              <Box component="span" sx={{ fontSize: 11.5, color: tokens.teal }}>
                ファクトチェック
              </Box>
              <Box component="span" sx={{ fontSize: 10.5, color: tokens.faint, fontWeight: 600 }}>
                {it.total_facts ?? 0}箇所中 {it.verified_facts ?? 0}件済み
              </Box>
            </Button>
          )}
          {sh.compliance && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/compliance?item=${it.id}`)}
              sx={{
                ...smallBtn,
                flex: 1,
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.25,
                py: 1,
                color: tokens.ink,
                borderColor: tokens.line,
              }}
            >
              <Box component="span" sx={{ fontSize: 11.5, color: tokens.indigo }}>
                規程チェック
              </Box>
              <Box component="span" sx={{ fontSize: 10.5, color: tokens.faint, fontWeight: 600 }}>
                抵触{bad} ・ 要確認{caution}
              </Box>
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  /** 案件1枚。共有の箱なので、中身（種別・件数）と誰がいつ入れたかだけ出す */
  /** 文書N / 動画N のチップ。帯に置くとタイトルが長いとき潰れるので、下の行に出す */
  const kindChip = (text: string, accent: boolean) => (
    <Box
      key={text}
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 800,
        px: 1,
        py: '3px',
        borderRadius: '6px',
        whiteSpace: 'nowrap',
        background: accent ? `${tokens.indigo}26` : `${tokens.teal}1f`,
        color: accent ? tokens.indigo : tokens.teal,
      }}
    >
      {text}
    </Box>
  );

  /** 案件1枚。共有の箱なので、中身（種別・件数）と誰がいつ入れたかだけ出す */
  const projectCard = (opts: {
    key: string;
    name: string;
    doc: number;
    video: number;
    total: number;
    mine: number;
    lastAdded: string | null;
    description?: string;
    onClick: () => void;
  }) => (
    <Box
      key={opts.key}
      onClick={opts.onClick}
      sx={{
        ...cardSx,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: '.15s',
        '&:hover': { borderColor: `${tokens.teal}73` },
      }}
    >
      {/* 帯。左端の線はグループの色にして、どのグループの案件か分かるようにする */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${tokens.line}`,
          // グラデだと左右で濃さが変わり、カードを並べたときばらついて見えるので単色にする
          background: 'rgba(59, 91, 219, 0.12)',
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '11px',
            background: tokens.paper,
            border: `1px solid ${tokens.line}`,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box component="img" src={icons.stepProject} alt="" sx={{ width: 25, height: 25 }} />
        </Box>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 15.5,
            fontWeight: 900,
            color: tokens.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {opts.name}
        </Typography>
        {opts.lastAdded && (
          <Typography sx={{ fontSize: 10.5, color: tokens.faint, whiteSpace: 'nowrap' }}>
            {formatDateTime(opts.lastAdded)}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
        <Typography sx={{ fontSize: 12.5, color: tokens.faint, whiteSpace: 'nowrap' }}>
          共有アイテム：
          <Box component="span" sx={{ fontSize: 18, fontWeight: 900, color: tokens.ink }}>
            {opts.total}
          </Box>
        </Typography>
        {opts.doc > 0 && kindChip(`文書${opts.doc}`, false)}
        {opts.video > 0 && kindChip(`動画${opts.video}`, true)}
        <Box sx={{ flex: 1 }} />
        {/* 自分が1件も入れていないときは出さない */}
        {opts.mine > 0 && (
          <Typography
            sx={{ fontSize: 11.5, fontWeight: 700, color: tokens.teal, whiteSpace: 'nowrap' }}
          >
            うち{opts.mine}件あなたが共有
          </Typography>
        )}
      </Box>

      {/* 説明は補足なので一番下。書かれていなければ行ごと出さない */}
      {opts.description?.trim() && (
        <Typography
          sx={{
            fontSize: 11.5,
            color: tokens.muted,
            lineHeight: 1.7,
            px: 2,
            pb: 1.75,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {opts.description}
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{ height: 'calc(var(--app-vh) - 64px - 64px)', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 900, color: tokens.ink }}>
          グループ共有
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<LinkIcon sx={{ fontSize: 17 }} />}
          onClick={() => navigate('/settings?tab=groups')}
          sx={{
            ...smallBtn,
            fontSize: 13,
            px: 2,
            py: 1.1,
            color: tokens.ink,
            borderColor: tokens.line,
          }}
        >
          招待リンク
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 17 }} />}
          onClick={() => navigate('/settings?tab=groups')}
          sx={{
            ...smallBtn,
            fontSize: 13,
            px: 2,
            py: 1.1,
            color: '#fff',
            background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
            boxShadow: '0 8px 20px rgba(40,50,90,.2)',
            '&:hover': { background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})` },
          }}
        >
          グループを作る
        </Button>
      </Box>

      {/* 使い方。初めての人に流れを伝えるので畳まず常に出す。
          背景は色ではなく薄い斜線にしてある（手順アイコンが緑と紫を持っているため、
          面に色を敷くと必ずぶつかる）。 */}
      <Box
        sx={{
          ...cardSx,
          mb: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2.5,
          py: 1.75,
          background:
            themeMode === 'dark'
              ? 'repeating-linear-gradient(135deg, transparent, transparent 9px, rgba(255,255,255,.03) 9px, rgba(255,255,255,.03) 10px)'
              : 'repeating-linear-gradient(135deg, transparent, transparent 9px, rgba(20,24,40,.022) 9px, rgba(20,24,40,.022) 10px)',
        }}
      >
        <Box
          component="img"
          src={IMG.workspace.owl}
          alt=""
          sx={{
            width: 64,
            height: 64,
            objectFit: 'contain',
            flexShrink: 0,
            filter: 'drop-shadow(0 5px 12px rgba(30,30,70,.16))',
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            sx={{ fontSize: 13.5, fontWeight: 900, color: tokens.ink, whiteSpace: 'nowrap' }}
          >
            グループでアイテムを共有する
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: tokens.muted, lineHeight: 1.7 }}>
            グループを作って人を招き、
            <br />
            案件ごとにアイテムをまとめます。
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.25,
          }}
        >
          {STEPS.map((st, i) => (
            <Box
              key={st.title}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                borderRadius: '11px',
                border: `1px solid ${tokens.line}`,
                background: tokens.paper,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: `1.5px solid ${tokens.teal}`,
                  color: tokens.teal,
                  fontSize: 11,
                  fontWeight: 900,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Box
                component="img"
                src={icons[st.icon]}
                alt=""
                sx={{ width: 30, height: 30, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: tokens.ink }}>
                  {st.title}
                </Typography>
                <Typography sx={{ fontSize: 10, color: tokens.faint, lineHeight: 1.55 }}>
                  {st.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* 左にグループ、真ん中に案件、右にメンバーと設定 */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '230px 1fr', lg: '230px 1fr 280px' },
          gridTemplateRows: 'minmax(0, 1fr)',
          gap: 2,
        }}
      >
        {/* 左: グループ */}
        <Box sx={{ ...cardSx, minHeight: 0, overflow: 'auto' }}>
          <Typography
            sx={{
              fontSize: 9.5,
              fontWeight: 900,
              letterSpacing: '.09em',
              color: tokens.faint,
              px: 1.75,
              pt: 1.75,
              pb: 0.75,
            }}
          >
            グループ
          </Typography>
          {noGroups ? (
            <Box sx={{ ...groupRowSx(true), opacity: 0.74, filter: 'saturate(.6)' }}>
              {avatar('見本グループ', '#0d9488')}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: tokens.ink }}>
                  見本グループ
                </Typography>
                <Typography sx={{ fontSize: 10, color: tokens.faint }}>案件3 ・ 3人</Typography>
              </Box>
            </Box>
          ) : (
            groups.map((g) => {
              const n = workspaces.filter((w: Workspace) =>
                (w.groupIds ?? []).includes(g.id),
              ).length;
              return (
                <Box
                  key={g.id}
                  onClick={() => setSelectedId(g.id)}
                  sx={groupRowSx(current?.id === g.id)}
                >
                  {avatar(g.name, g.color)}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: tokens.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {g.name}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: tokens.faint }}>
                      案件{n} ・ {(members[g.id] ?? []).length}人
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
          <Box
            onClick={() => navigate('/settings?tab=groups')}
            sx={{ ...groupRowSx(false), color: tokens.muted }}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: `1px dashed ${tokens.line}`,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <AddIcon sx={{ fontSize: 15, color: tokens.muted }} />
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800 }}>グループを作る</Typography>
          </Box>
        </Box>

        {/* 真ん中: 案件の一覧、または開いている案件の中身 */}
        <Box sx={{ ...cardSx, minHeight: 0, overflow: 'auto', p: 2 }}>
          {openProject ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Button
                  onClick={() => navigate('/share')}
                  sx={{ ...smallBtn, color: tokens.muted, px: 1 }}
                >
                  ← 案件一覧
                </Button>
                <ChevronRightIcon sx={{ fontSize: 15, color: tokens.faint }} />
                <Typography sx={{ fontSize: 11.5, color: tokens.muted }}>
                  {current?.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 900, color: tokens.ink }}>
                  {openProject.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: tokens.faint }}>
                  共有アイテム：{projectItems?.length ?? 0}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={openShareDialog}
                  sx={{ ...smallBtn, ...gradBtn }}
                >
                  アイテムを共有
                </Button>
              </Box>
              {projectItems === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : projectItems.length === 0 ? (
                <Typography
                  sx={{ fontSize: 12.5, color: tokens.faint, px: 0.5, py: 2, lineHeight: 1.9 }}
                >
                  この案件にはまだアイテムが共有されていません。
                  <br />
                  マイアイテムで ⋮ を押し、「案件に共有」から追加できます。
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
                    gap: 1.5,
                  }}
                >
                  {projectItems.map(sharedItemCard)}
                </Box>
              )}
            </>
          ) : noGroups ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 2.5,
                  mb: 2,
                  borderRadius: '12px',
                  border: `1.5px dashed ${tokens.line}`,
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 900, color: tokens.ink }}>
                  まだグループがありません
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: tokens.muted, textAlign: 'center', lineHeight: 1.8 }}
                >
                  グループを作ると、ここにメンバーと案件が並びます。
                  <br />
                  下の見本は押して中まで見られます。
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 17 }} />}
                  onClick={() => navigate('/settings?tab=groups')}
                  sx={{ ...smallBtn, mt: 0.5, fontSize: 13, px: 2.25, py: 1.1, ...gradBtn }}
                >
                  最初のグループを作る
                </Button>
              </Box>
              <Box sx={{ opacity: 0.74, filter: 'saturate(.6)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                  {avatar('見本グループ', '#0d9488')}
                  <Typography sx={{ fontSize: 15, fontWeight: 900, color: tokens.ink }}>
                    見本グループ
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 800,
                      px: 1,
                      py: '2px',
                      borderRadius: '999px',
                      background: 'rgba(224,134,0,.16)',
                      color: '#e08600',
                    }}
                  >
                    見本
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 400px), 1fr))',
                    gap: 1.5,
                  }}
                >
                  {SAMPLE_PROJECTS.map((pj) =>
                    projectCard({
                      key: pj.id,
                      name: pj.name,
                      doc: pj.doc,
                      video: pj.video,
                      total: pj.total,
                      mine: pj.mine,
                      lastAdded: pj.date,
                      description: pj.description,
                      onClick: () => navigate('/share/sample'),
                    }),
                  )}
                </Box>
              </Box>
            </>
          ) : current ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
                {avatar(current.name, current.color)}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15.5, fontWeight: 900, color: tokens.ink }}>
                    {current.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: tokens.faint }}>
                    メンバー{(members[current.id] ?? []).length}人
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => openCreateFor(current.id)}
                  sx={{ ...smallBtn, ...gradBtn }}
                >
                  案件を作る
                </Button>
              </Box>
              {(() => {
                const mine = workspaces.filter((w: Workspace) =>
                  (w.groupIds ?? []).includes(current.id),
                );
                // 案件がまだ無い人には、どんな箱なのかを見本で見せる。
                // グループを作った直後が「案件とは何か」を一番知りたいタイミングのため。
                if (mine.length === 0) {
                  return (
                    <>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: tokens.faint,
                          px: 0.5,
                          pb: 1.75,
                          lineHeight: 1.8,
                        }}
                      >
                        このグループにはまだ案件がありません。「案件を作る」から始めてみましょう。
                        <br />
                        下は見本です。押すと中まで見られます。
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 400px), 1fr))',
                          gap: 1.5,
                          opacity: 0.74,
                          filter: 'saturate(.6)',
                        }}
                      >
                        {SAMPLE_PROJECTS.slice(0, 2).map((pj) =>
                          projectCard({
                            key: pj.id,
                            name: pj.name,
                            doc: pj.doc,
                            video: pj.video,
                            total: pj.total,
                            mine: pj.mine,
                            lastAdded: pj.date,
                            description: pj.description,
                            onClick: () => navigate('/share/sample'),
                          }),
                        )}
                      </Box>
                    </>
                  );
                }
                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 400px), 1fr))',
                      gap: 1.5,
                    }}
                  >
                    {mine.map((w) => {
                      const sm = summaries[w.id];
                      return projectCard({
                        key: w.id,
                        name: w.name,
                        doc: sm?.doc ?? 0,
                        video: sm?.video ?? 0,
                        total: sm?.total ?? 0,
                        mine: sm?.mine ?? 0,
                        lastAdded: sm?.lastAdded ?? null,
                        description: w.description,
                        onClick: () => navigate(`/share/${w.id}`),
                      });
                    })}
                  </Box>
                );
              })()}
            </>
          ) : null}
        </Box>

        {/* 右: メンバーとグループの設定。共有の話をこの画面で完結させる */}
        <Box
          sx={{ ...cardSx, minHeight: 0, overflow: 'auto', display: { xs: 'none', lg: 'block' } }}
        >
          {current ? (
            <>
              <Typography sx={{ ...paneHeadSx }}>
                メンバー {(members[current.id] ?? []).length}人
              </Typography>
              {(members[current.id] ?? []).map((m) => (
                <Box
                  key={m.userId}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.1 }}
                >
                  {avatar(m.displayName || '?', current.color)}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: tokens.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {m.displayName || '（名前なし）'}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: tokens.faint }}>
                      {m.role === 'owner' ? 'オーナー' : 'メンバー'}
                    </Typography>
                  </Box>
                  {m.role !== 'owner' && isOwner && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveMember(current.id, m.userId)}
                      sx={{ color: tokens.faint }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              ))}

              <Box
                sx={{ m: 1.75, p: 1.5, border: `1px dashed ${tokens.line}`, borderRadius: '10px' }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: tokens.ink, mb: 0.75 }}>
                  招待リンク
                </Typography>
                {inviteUrl ? (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: tokens.faint,
                      background: 'transparent',
                      border: `1px solid ${tokens.line}`,
                      borderRadius: '7px',
                      px: 1,
                      py: 0.75,
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {inviteUrl}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 10.5, color: tokens.faint, lineHeight: 1.7, mb: 1 }}>
                    リンクを作って送ると、このグループに参加できます（7日間有効）。
                  </Typography>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  disabled={inviting}
                  onClick={() => handleInvite(current.id)}
                  sx={{ ...smallBtn, ...gradBtn }}
                >
                  {inviting ? '作成中...' : inviteUrl ? 'リンクをコピー' : '招待リンクを作る'}
                </Button>
              </Box>

              {isOwner && (
                <>
                  <Typography sx={{ ...paneHeadSx }}>グループの設定</Typography>
                  <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/settings?tab=groups')}
                      sx={{ ...smallBtn, color: tokens.ink, borderColor: tokens.line }}
                    >
                      名前と色を変える
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleDeleteGroup(current.id, current.name)}
                      sx={{ ...smallBtn, color: '#d4433d', borderColor: 'rgba(212,67,61,.42)' }}
                    >
                      グループを削除
                    </Button>
                  </Box>
                </>
              )}
            </>
          ) : (
            <Typography sx={{ ...paneHeadSx }}>メンバー</Typography>
          )}
        </Box>
      </Box>

      {/* 案件から自分のアイテムを選んで共有する */}
      <AppDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        icon={<Box component="img" src={icons.folders} alt="" />}
        title="アイテムを共有"
        subtitle="自分のアイテムから選び、どの結果を見せるか決めます"
        size="md"
        footerNote={`${Object.values(picked).filter((v) => v.factCheck || v.compliance).length}件を共有します`}
        actions={
          <>
            <Button onClick={() => setShareOpen(false)} sx={{ ...smallBtn, color: tokens.muted }}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={handleSharePicked}
              disabled={sharing || !Object.values(picked).some((v) => v.factCheck || v.compliance)}
              sx={{ ...smallBtn, px: 2.25, py: 1, ...gradBtn }}
            >
              {sharing ? '共有中...' : '共有する'}
            </Button>
          </>
        }
      >
        {myItems === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          myItems
            .filter((it) => !(it.sharedWorkspaceIds ?? []).includes(openProjectId ?? ''))
            .map((it) => {
              const cur = picked[it.id] ?? { factCheck: false, compliance: false };
              const hasCompliance = !!it.complianceCheckedAt;
              const set = (next: { factCheck: boolean; compliance: boolean }) =>
                setPicked((prev) => ({ ...prev, [it.id]: next }));
              return (
                <Box
                  key={it.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    py: 1.25,
                    borderTop: `1px solid ${tokens.line}`,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 800,
                        color: tokens.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.title}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: tokens.faint }}>
                      {it.type.startsWith('video') ? '動画' : '文書'} ・{' '}
                      {formatDateTime(it.createdAt)}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant={cur.factCheck ? 'contained' : 'outlined'}
                    onClick={() => set({ ...cur, factCheck: !cur.factCheck })}
                    sx={{
                      ...smallBtn,
                      fontSize: 10.5,
                      ...(cur.factCheck
                        ? gradBtn
                        : { color: tokens.muted, borderColor: tokens.line }),
                    }}
                  >
                    ファクトチェック
                  </Button>
                  <Button
                    size="small"
                    variant={cur.compliance ? 'contained' : 'outlined'}
                    disabled={!hasCompliance}
                    onClick={() => set({ ...cur, compliance: !cur.compliance })}
                    sx={{
                      ...smallBtn,
                      fontSize: 10.5,
                      ...(cur.compliance
                        ? gradBtn
                        : { color: tokens.muted, borderColor: tokens.line }),
                    }}
                  >
                    規程チェック
                  </Button>
                </Box>
              );
            })
        )}
      </AppDialog>

      <AppDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        icon={<Box component="img" src={icons.stepProject} alt="" />}
        title="新しい案件を作成"
        subtitle="放送回や案件ごとに箱を分けます"
        actions={
          <>
            <Button
              onClick={() => setCreateDialogOpen(false)}
              sx={{ ...smallBtn, color: tokens.muted }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateWorkspace}
              disabled={creating || !newName.trim() || selectedGroupIds.length === 0}
              sx={{ ...smallBtn, px: 2.25, py: 1, ...gradBtn }}
            >
              {creating ? '作成中...' : '作成'}
            </Button>
          </>
        }
      >
        <DialogField
          label="案件名"
          placeholder="例: 0913 フーシ派 / 12/24放送分"
          value={newName}
          onChange={setNewName}
          required
        />
        <DialogField
          label="説明"
          placeholder="例: 9/13放送分の裏どり資料をまとめています"
          value={newDescription}
          onChange={setNewDescription}
          multiline
          rows={2}
        />
        <DialogField label="共有グループ" placeholder="" value="" onChange={() => {}} required>
          <Select
            multiple
            size="small"
            fullWidth
            displayEmpty
            value={selectedGroupIds}
            onChange={(e) => setSelectedGroupIds(e.target.value as string[])}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <Box component="span" sx={{ color: tokens.faint, fontSize: 13 }}>
                  例: 制作1班
                </Box>
              ) : (
                selected.map((id) => groups.find((g) => g.id === id)?.name ?? '').join(', ')
              )
            }
            sx={{ borderRadius: '11px', fontSize: 13 }}
          >
            {groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                <Checkbox checked={selectedGroupIds.includes(group.id)} />
                <ListItemText primary={group.name} />
              </MenuItem>
            ))}
          </Select>
        </DialogField>
      </AppDialog>
    </Box>
  );
};

export default WorkspaceList;
